import React, { createContext, useContext, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../design/dialog";
import { Button } from "../design/button";
import { useIncome } from "../contexts/IncomeContext";
import { Field, FieldGroup, Fieldset, Label } from "../design/fieldset";
import { Input } from "../design/input";
import { Textarea } from "../design/textarea";
import { Participant } from "../models/Participant";
import { IncomeType } from "../models/IncomeType";

interface Context {
  openDialog: (
    participantId?: Participant["_id"],
    incomeType?: IncomeType,
  ) => void;
  closeDialog: () => void;
  isOpen: boolean;
}

const CreateIncomeDialogProviderContext = createContext<Context | undefined>(
  undefined,
);

export const CreateIncomeDialogProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { createIncome } = useIncome();
  const [participantId, setParticipantId] = useState<
    Participant["_id"] | undefined
  >(undefined);
  const [isOpen, setIsOpen] = useState(false);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState<IncomeType | undefined>(undefined);
  const [amount, setAmount] = useState(0);
  const [grossAnnualIncome, setGrossAnnualIncome] = useState(0);
  const [grossMonthlyIncome, setGrossMonthlyIncome] = useState(0);

  const openDialog = (
    participantId?: Participant["_id"],
    incomeType?: IncomeType,
  ) => {
    setParticipantId(participantId);
    setType(incomeType);
    setIsOpen(true);
  };

  const closeDialog = () => {
    setIsOpen(false);
    setParticipantId(undefined);
    setType(undefined);
  };

  const handleYes = async () => {
    if (!type) return;
    try {
      await createIncome({
        participantId: participantId,
        type,
        name,
        description,
        amount,
        grossAnnualIncome,
        grossMonthlyIncome,
        isJoint: !participantId ? true : false,
      });
      closeDialog();
    } catch (error) {
      console.error("Error completing income:", error);
      alert("Failed to complete income. Please try again later.");
    }
  };

  return (
    <CreateIncomeDialogProviderContext.Provider
      value={{ openDialog, closeDialog, isOpen }}
    >
      <Dialog open={isOpen} onClose={closeDialog}>
        <DialogTitle>Create Income</DialogTitle>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Name</Label>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter task name"
                />
              </Field>
              <Field>
                <Label>Description</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter task description"
                />
              </Field>
              <Field>
                <Label>Amount</Label>
                <Input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(Number(e.target.value))}
                  placeholder="Enter amount"
                />
              </Field>
              <Field>
                <Label>Gross Annual Income</Label>
                <Input
                  type="number"
                  value={grossAnnualIncome}
                  onChange={(e) => setGrossAnnualIncome(Number(e.target.value))}
                  placeholder="Enter gross annual income"
                />
              </Field>
              <Field>
                <Label>Gross Monthly Income</Label>
                <Input
                  type="number"
                  value={grossMonthlyIncome}
                  onChange={(e) =>
                    setGrossMonthlyIncome(Number(e.target.value))
                  }
                  placeholder="Enter gross monthly income"
                />
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={closeDialog}>
            Cancel
          </Button>
          <Button onClick={handleYes}>Save</Button>
        </DialogActions>
      </Dialog>
      {children}
    </CreateIncomeDialogProviderContext.Provider>
  );
};

export const useCreateIncomeDialog = () => {
  const ctx = useContext(CreateIncomeDialogProviderContext);
  if (!ctx)
    throw new Error(
      "useCreateIncomeDialog must be used inside CreateIncomeDialogProvider",
    );
  return ctx;
};
